import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { useQuery, useMutation } from '@apollo/client/react';
import { createURLFromLocation } from '@jwp/ott-ui-react/src/utils/location';
import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import { GET_MY_VIDEOS, GetMyVideosData, GetMyVideosVariables, MyVideo } from '@jwp/ott-common/src/graphql/query/reels.queries';
import { DELETE_MEDIA_BY_MEDIA_ID, DeleteMediaByMediaIdData } from '@jwp/ott-common/src/graphql/mutation/reels.mutation';
import BackwardArrow from '@jwp/ott-theme/assets/icons/arrow_backward.svg';
import PlusIcon from '@jwp/ott-theme/assets/icons/plus.svg?react';
import CheckIcon from '@jwp/ott-theme/assets/icons/check.svg?react';
import InfoIcon from '@jwp/ott-theme/assets/icons/info.svg?react';
import EditIcon from '@jwp/ott-theme/assets/icons/edit.svg?react';
import DeleteIcon from '@jwp/ott-theme/assets/icons/delete.svg?react';
import env from '@jwp/ott-common/src/env';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';
import { useUploadStore } from '@jwp/ott-common/src/stores/UploadStore';
import { PATH_VIDEO_PREVIEW, PATH_HOME, PATH_MY_VIDEO_DETAILS } from '@jwp/ott-common/src/paths';

import { useAccountStore } from '../../../../common/src/stores/AccountStore';
import { useTheme } from '../../../../../ThemeContext';
import IconButton from '../IconButton/IconButton';
import Button from '../Button/Button';
import Icon from '../Icon/Icon';
import Popover from '../Popover/Popover';
import ConfirmationDialog from '../ConfirmationDialog/ConfirmationDialog';
import Dialog from '../Dialog/Dialog';
import Card from '../Card/Card';
import Alert from '../Alert/Alert';

import styles from './MyVideos.module.scss';



const MyVideos = () => {
  const { t } = useTranslation(['user', 'video', 'common']);
  const { universalStyles } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const [activeTab, setActiveTab] = useState<'reels' | 'videos'>(queryParams.get('tab') === 'videos' ? 'videos' : 'reels');
  const user = useAccountStore((state) => state.user);
  const isLoggedIn = !!user;
  const { isUploadEnabled } = useAppConfig();
  const setPreviewUrl = useUploadStore((state) => state.setPreviewUrl);
  const setValues = useUploadStore((state) => state.setValues);
  const setReturnPath = useUploadStore((state) => state.setReturnPath);
  const setMediaId = useUploadStore((state) => state.setMediaId);
  const setDuration = useUploadStore((state) => state.setDuration);
  const setThumbnail = useUploadStore((state) => state.setThumbnail);
  const setInitiated = useUploadStore((state) => state.setInitiated);
  const resetUploadStore = useUploadStore((state) => state.reset);

  useEffect(() => {
    const tab = queryParams.get('tab');
    if (tab === 'videos' || tab === 'reels') {
      setActiveTab(tab);
    } else if (!tab) {
      setActiveTab('reels');
    }
  }, [queryParams]);

  const u = queryParams.get('u');
  const [lastU, setLastU] = useState<string | null>(u);

  const [menuAnchor, setMenuAnchor] = useState<{ x: number, y: number, isBottom?: boolean } | null>(null);
  const [selectedItem, setSelectedItem] = useState<MyVideo | null>(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const getQueryVariables = (type: string): GetMyVideosVariables => ({
    filter: {
      maType: { equalTo: type },
      maUploadStatus: { equalTo: 'UPLOADED' },
      maDeletedAt: { isNull: true },
      maUploadedBy: { equalTo: user?.id || '' },
    },
    first: 12,
    offset: 0,
  });

  const reelsQuery = useQuery<GetMyVideosData, GetMyVideosVariables>(GET_MY_VIDEOS, {
    variables: getQueryVariables('REELS'),
    skip: !user?.id,
    notifyOnNetworkStatusChange: true,
  });

  const videosQuery = useQuery<GetMyVideosData, GetMyVideosVariables>(GET_MY_VIDEOS, {
    variables: getQueryVariables('VIDEOS'),
    skip: !user?.id,
    notifyOnNetworkStatusChange: true,
  });

  const [deleteMediaApproval, { loading: deleteLoading }] = useMutation<DeleteMediaByMediaIdData, { maId: string }>(DELETE_MEDIA_BY_MEDIA_ID, {
    onCompleted: () => {
      setConfirmationOpen(false);
      setSuccessOpen(true);
      reelsQuery.refetch();
      videosQuery.refetch();
    },
    onError: (error) => {
      console.error('Delete failed:', error);
      setErrorMessage(t('video:delete_error', { defaultValue: 'Failed to delete video. Please try again.' }));
      setErrorOpen(true);
    }
  });

  useEffect(() => {
    if ((lastU === 'upload-success' || lastU === 'edit-success') && !u) {
      reelsQuery.refetch();
      videosQuery.refetch();
    }
    setLastU(u);
  }, [u, lastU, reelsQuery, videosQuery]);

  useEffect(() => {
    const handleResize = () => setMenuAnchor(null);
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize, true); // Close on scroll too as it's fixed position
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize, true);
    };
  }, []);


  const handleBackClick = () => {
    navigate(PATH_HOME, { replace: true });
  };

  const handleUploadClick = () => {
    resetUploadStore();
    setInitiated(true);
    navigate(createURLFromLocation(location, { u: 'upload-reel' }));
  };

  const currentQuery = activeTab === 'reels' ? reelsQuery : videosQuery;
  const items = currentQuery.data?.allMediaApprovals?.nodes || [];
  const totalCount = currentQuery.data?.allMediaApprovals?.totalCount || 0;
  const loading = currentQuery.loading;

  const handleLoadMore = () => {
    if (items.length >= totalCount) return;
    currentQuery.fetchMore({
      variables: { offset: items.length },
      updateQuery: (prevResult: GetMyVideosData, { fetchMoreResult }: { fetchMoreResult?: GetMyVideosData }) => {
        if (!fetchMoreResult) return prevResult;
        return {
          allMediaApprovals: {
            ...fetchMoreResult.allMediaApprovals,
            nodes: [
              ...(prevResult.allMediaApprovals?.nodes || []),
              ...(fetchMoreResult.allMediaApprovals?.nodes || []),
            ],
          },
        };
      },
    });
  };

  const handleShowDetails = () => {
    setMenuAnchor(null);
    if (selectedItem) {
      setInitiated(true);
      navigate(createURLFromLocation(location, { u: 'reel-details', id: selectedItem.maId }));
    }
  };

  const handleEdit = () => {
    setMenuAnchor(null);
    if (selectedItem) {
      setMediaId(selectedItem.maId);
      setPreviewUrl(`${env.APP_BASE_URL || ''}${selectedItem.maS3MediaKeyUrl}`);
      setThumbnail(null, `${env.APP_BASE_URL || ''}${selectedItem.maS3ThumbnailKeyUrl}`);
      setDuration(selectedItem.maDuration ? Number(selectedItem.maDuration) : null);
      setValues({
        title: selectedItem.maTitle,
        description: selectedItem.maDescription,
        category: selectedItem.maCategory,
        tags: Array.isArray(selectedItem.maTags) ? selectedItem.maTags.join(', ') : (typeof selectedItem.maTags === 'string' ? selectedItem.maTags : ''),
      });
      setReturnPath(location.pathname + location.search);
      setInitiated(true);
      navigate(createURLFromLocation(location, { u: selectedItem.maType === 'REELS' ? 'upload-reel' : 'upload-video' }));
    }
  };

  const handleDelete = () => {
    setMenuAnchor(null);
    setConfirmationOpen(true);
  };

  const reelsCount = reelsQuery.data?.allMediaApprovals?.totalCount || 0;
  const videosCount = videosQuery.data?.allMediaApprovals?.totalCount || 0;

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.headerTitlePart}>
          <IconButton className={styles.iconButton} onClick={handleBackClick}>
            <img src={BackwardArrow} alt="Back" />
          </IconButton>
          <h1>{t('nav.videos')}</h1>
        </div>
      </header>

      <div className={styles.actionsBar}>
        <div className={styles.tabs}>
          <Button
            label={t('video:reels_count', { count: reelsCount })}
            className={classNames(styles.tab, { [styles.activeTab]: activeTab === 'reels' })}
            onClick={() => {
              setActiveTab('reels');
              navigate(createURLFromLocation(location, { tab: null }));
            }}
            variant="text"
            active={activeTab === 'reels'}
          />
          <Button
            label={t('video:videos_count', { count: videosCount })}
            className={classNames(styles.tab, { [styles.activeTab]: activeTab === 'videos' })}
            onClick={() => {
              setActiveTab('videos');
              navigate(createURLFromLocation(location, { tab: 'videos' }));
            }}
            variant="text"
            active={activeTab === 'videos'}
          />
        </div>

        {isLoggedIn && isUploadEnabled && (
          <Button
            label={activeTab === 'reels' ? t('video:upload_reel') : t('upload_video.title', { defaultValue: 'Upload Video' })}
            startIcon={<Icon icon={PlusIcon} />}
            onClick={() => {
              resetUploadStore();
              setInitiated(true);
              navigate(createURLFromLocation(location, { u: activeTab === 'reels' ? 'upload-reel' : 'upload-video' }));
            }}
            className={styles.uploadButton}
            variant="contained"
            color="primary"
          />
        )}
      </div>

      {items.length === 0 && !loading ? (
        <p className={styles.emptyVideos} style={universalStyles.primaryTextColor}>
          {t('video:no_videos')}
        </p>
      ) : (
        <div className={classNames(styles.grid, { [styles.reelsGrid]: activeTab === 'reels', [styles.videosGrid]: activeTab === 'videos' })}>
          {items.map((item) => {
            const cardItem: PlaylistItem = {
              mediaid: item.maId,
              title: item.maTitle,
              duration: activeTab === 'reels' && item.maDuration ? Number(item.maDuration) / 1000 : 0,
              cardImage: `${env.APP_BASE_URL || ''}${item.maS3ThumbnailKeyUrl}`,
              images: [],
              link: '',
              pubdate: 0,
              sources: [],
              feedid: '',
              description: item.maDescription,
            } as unknown as PlaylistItem;

            return (
              <Card
                key={item.maId}
                item={cardItem}
                url="#"
                posterAspect={activeTab === 'reels' ? '9:16' : '16:9'}
                status={item.maStatus}
                isReels={activeTab === 'reels'}
                isOverlayTestVisible={true}
                onMenuClick={

                  (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (menuAnchor && selectedItem?.maId === item.maId) {
                      setMenuAnchor(null);
                    } else {
                      setSelectedItem(item);
                      const rect = e.currentTarget.getBoundingClientRect();
                      const popoverHeight = 250;
                      const isBottom = rect.bottom + popoverHeight > window.innerHeight;
                      setMenuAnchor({ x: rect.right, y: isBottom ? rect.top : rect.bottom, isBottom });
                    }
                  }
                }
                onClick={(e) => {
                  e.preventDefault();
                  if (activeTab === 'reels') {
                    setPreviewUrl(`${env.APP_BASE_URL || ''}${item.maS3MediaKeyUrl}`);
                    setValues({
                      title: item.maTitle,
                      description: item.maDescription,
                      category: item.maCategory,
                      tags: Array.isArray(item.maTags) ? item.maTags.join(', ') : typeof item.maTags === 'string' ? item.maTags : '',
                    });
                    setReturnPath(location.pathname + location.search);
                    navigate(`${PATH_VIDEO_PREVIEW}/${item.maId}?returnPath=${encodeURIComponent(location.pathname + location.search)}`);
                  } else {
                    navigate(`${PATH_MY_VIDEO_DETAILS}/${item.maId}?returnPath=${encodeURIComponent(location.pathname + location.search)}`);
                  }
                }}
              />
            );
          })}
        </div>
      )}

      {items.length < totalCount && (
        <div className={styles.loadMoreContainer} style={{ textAlign: 'center', marginTop: '32px' }}>
          <Button label={loading ? t('common:loading') : t('common:load_more')} onClick={handleLoadMore} disabled={loading} variant="outlined" />
        </div>
      )}

      {/* Popover Menu */}
      <Popover
        isOpen={!!menuAnchor}
        onClose={() => setMenuAnchor(null)}
        className={classNames(styles.popoverNoGap, menuAnchor?.isBottom && styles.popoverUp)}
        style={{
          position: 'fixed',
          top: menuAnchor?.y || 0,
          left: menuAnchor?.x || 0,
          transform: `translateX(-100%) ${menuAnchor?.isBottom ? 'translateY(-100%)' : ''}`,
          zIndex: 1000,
        }}
      >

        <div className={styles.popoverMenu}>
          {activeTab === 'reels' && (
            <><button className={styles.popoverItem} onClick={handleShowDetails}>
              <Icon icon={InfoIcon} className={styles.popoverIcon} />
              <span>{t('video:show_details')}</span>
            </button><div className={styles.popoverDivider} /><button className={styles.popoverItem} onClick={handleEdit}>
                <Icon icon={EditIcon} className={styles.popoverIcon} />
                <span>{activeTab === 'reels' ? t('video:edit_reel') : t('video:edit_video')}</span>
              </button>
              <div className={styles.popoverDivider} />
            </>
          )}


          <button className={classNames(styles.popoverItem, styles.deleteItem)} onClick={handleDelete}>
            <Icon icon={DeleteIcon} className={styles.popoverIcon} />
            <span>{activeTab === 'reels' ? t('video:delete_reel') : t('video:delete_video')}</span>
          </button>
        </div>
      </Popover>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={confirmationOpen}
        variant="delete"
        title={activeTab === 'reels' ? t('video:delete_reel_title') : t('video:delete_video_title')}
        body={activeTab === 'reels' ? t('video:delete_reel_body') : t('video:delete_video_body')}
        confirmLabel={activeTab === 'reels' ? t('video:delete_reel_confirm') : t('video:delete_video_confirm')}
        closeLabel={t('common:no')}
        onConfirm={() => {
          if (selectedItem) {
            deleteMediaApproval({ variables: { maId: selectedItem.maId } });
          }
        }}
        onClose={() => setConfirmationOpen(false)}
        busy={deleteLoading}
      />

      {/* Success Dialog */}
      <Dialog open={successOpen} onClose={() => setSuccessOpen(false)} role="alertdialog">
        <div className={styles.successDialog}>
          <div className={styles.successIconContainer}>
            <Icon icon={CheckIcon} />
          </div>
          <h2>{activeTab === 'reels' ? t('upload:delete-success.title') : t('upload:delete-success-video.title')}</h2>
          <p>{activeTab === 'reels' ? t('upload:delete-success.description') : t('upload:delete-success-video.description')}</p>
          <Button label={t('common:okay', { defaultValue: 'Okay' })} variant="contained" color="primary" onClick={() => setSuccessOpen(false)} fullWidth />
        </div>
      </Dialog>

      <Alert open={errorOpen} message={errorMessage} onClose={() => setErrorOpen(false)} />
    </div>
  );
};

export default MyVideos;
